<script lang="ts">
  import { media } from "$lib"
  import { getNavContext } from "$lib/context"
  import { ChevronRight } from "@tastyworks/icons-2.0"
  import { Accordion } from "@tastyworks/ui-library"
  import { onMount } from "svelte"
  import { _ } from "svelte-i18n"
  import type { Readable } from "svelte/store"
  import { slide } from "svelte/transition"
  import SideNavGroup from "./SideNavGroup.svelte"
  import HelpCenter from "./help-center/HelpCenter.svelte"

  interface Props {
    inMobileMenu?: boolean
    cobrowseEnabled?: Readable<boolean>
  }

  let { inMobileMenu = false, cobrowseEnabled }: Props = $props()

  const nav = getNavContext()

  const visibleNavGroups = $derived(
    nav.navGroups.filter(
      (navGroup) =>
        !navGroup.hidden &&
        navGroup.items.some((item) => !item.hidden) &&
        navGroup.id !== "done"
    )
  )

  const value = $derived(handleGroupUpdate(nav.currentStepNode.groupIndex))
  let open: boolean = $state(false)

  function useAction(node: HTMLElement) {
    node.addEventListener("click", () => {
      open = true
    })
  }

  function handleGroupUpdate(groupIndex: number) {
    return $media.md || inMobileMenu ? groupIndex.toString() : null
  }

  let animate = $state(false)
  onMount(() => {
    // onMount allows the animation to circumvent the initial ssr render
    animate = true
  })
</script>

<Accordion.Root role="navigation" class="relative z-20" type="single" {value}>
  {#if animate && ($media.md || open || inMobileMenu)}
    {#if inMobileMenu}
      <div class="px-4" role="menubar" aria-orientation="vertical">
        {#each visibleNavGroups as navGroup, groupIndex (navGroup.id)}
          <SideNavGroup
            {navGroup}
            {groupIndex}
            bind:isGroupOpen={open}
            {inMobileMenu}
          />
        {/each}
      </div>
    {:else}
      <div
        class="md:py-3"
        transition:slide
        role="menubar"
        aria-orientation="vertical"
      >
        {#each visibleNavGroups as navGroup, groupIndex (navGroup.id)}
          <SideNavGroup {navGroup} {groupIndex} bind:isGroupOpen={open} />
        {/each}
      </div>
    {/if}
  {/if}
  {#if !inMobileMenu}
    <!-- closed label -->
    <Accordion.Item
      class="
        border-none
        transition-opacity
        data-[hidden=true]:hidden
        data-[hidden=true]:opacity-0
        md:hidden"
      value={nav.currentStepNode.groupIndex}
      data-hidden={open}
      use={[useAction]}
    >
      <Accordion.Trigger class="px-4 py-3 font-large-500">
        <span class="text-general-secondary-text">
          {$_("common.navStep")}
          {nav.currentStepNode.groupIndex + 1}:&nbsp;
        </span>
        {$_(`${nav.getGroupId(nav.currentStepNode)}.navLabel`)}
        <div class="flex-1"></div>
        {#snippet icon()}<ChevronRight
            class="w-5 text-general-secondary-text"
          />{/snippet}
      </Accordion.Trigger>
    </Accordion.Item>
    <!-- overlay -->
    <div
      class="fixed z-10 h-screen w-screen bg-black md:hidden"
      class:hidden={!open}
      role="presentation"
      onclick={() => {
        open = false
      }}
    ></div>
  {/if}
  <!-- mobile view -->
  {#if !inMobileMenu && !$media.md && open}
    <HelpCenter {cobrowseEnabled} />
  {/if}
</Accordion.Root>

<!-- desktop view -->
{#if !inMobileMenu && $media.md}
  <HelpCenter {cobrowseEnabled} />
{/if}
